import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '../base/Text';
import { Button } from '../base/Button';
import { Spinner } from '../base/Spinner';
import { uploadFile } from '../../api/services';

const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'mp3', 'wav', 'm4a', 'aac'];

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

interface AddFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AddFileModal = ({ isOpen, onClose, onSuccess }: AddFileModalProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError(null);

    if (file.size > MAX_FILE_SIZE) {
      setError(t('files.maxSizeError', 'File too large. Maximum 25MB.'));
      return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const isAllowed = ALLOWED_EXTENSIONS.includes(ext);
    if (!isAllowed) {
      setError(t('files.unsupportedType', 'This file type is not supported.'));
      return;
    }

    setIsUploading(true);
    try {
      await uploadFile(file);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('files.uploadFailed', 'Failed to upload file'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleClick = () => {
    if (!isUploading) inputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => e.target === e.currentTarget && !isUploading && onClose()}
    >
      <div
        style={{
          background: 'var(--color-bg-secondary)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-xl)',
          maxWidth: 400,
          width: '90%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginBottom: 'var(--space-lg)', color: 'var(--color-text-primary)' }}>
          {t('files.addFile', 'Add File')}
        </h2>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp,.heic,.mp3,.wav,.m4a,.aac,application/pdf,image/*,audio/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        {isUploading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-md)', padding: 'var(--space-xl)' }}>
            <Spinner size="lg" />
            <Text variant="body" muted>{t('files.uploading', 'Uploading...')}</Text>
          </div>
        ) : (
          <>
            <Button variant="primary" fullWidth onClick={handleClick} style={{ marginBottom: 'var(--space-md)' }}>
              {t('files.chooseFile', 'Choose File')}
            </Button>
            {error && (
              <Text variant="body" style={{ color: 'var(--color-error)', marginBottom: 'var(--space-md)' }}>
                {error}
              </Text>
            )}
            <Text variant="meta" muted>
              {t('files.maxSize', 'Maximum file size: 25MB')}
            </Text>
          </>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 'var(--space-lg)' }}>
          <Button variant="ghost" onClick={onClose} disabled={isUploading}>
            {t('common.cancel', 'Cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
};
